import type { Language, Locale, appLanguages } from '@seek/melways-sites';
import { VocabProvider } from '@vocab/react';
import React, { type ReactNode, useContext, useEffect, useState } from 'react';

export type SupportedLocale = Exclude<(typeof appLanguages)[number], string>;

const DEFAULT_LANGUAGE_AND_LOCALE: LanguageAndLocale = {
  language: 'en',
  locale: 'en-AU',
};

interface LanguageAndLocale {
  language: Language;
  locale: Locale;
}

interface LocalizationProviderProps {
  children: ReactNode;
  supportedLocales: SupportedLocale[];
  locale?: string;
}

/**
 * Parse a list of language and locale values to a singular SEEK supported language and locale pair.
 * @param languagesAndLocales The priority ordered list of languages and locales. The first value in this list should be the users first preference.
 * @param supportedLocales The list of support locales for the application
 * @returns Object containing the language and locale
 */
const parseLanguagesAndLocales = (
  languagesAndLocales: string[],
  supportedLocales: SupportedLocale[],
): LanguageAndLocale => {
  const valid = languagesAndLocales.flatMap((locale) => {
    const supportedLocale = supportedLocales.find(
      (l) => l.name === locale || l.extends === locale,
    );
    return supportedLocale ?? [];
  });

  if (valid.length === 0) {
    return DEFAULT_LANGUAGE_AND_LOCALE;
  }

  return {
    language: valid[0].extends,
    locale: valid[0].name,
  };
};

const ctx = React.createContext<LanguageAndLocale>(DEFAULT_LANGUAGE_AND_LOCALE);

export const LocalizationProvider = ({
  children,
  locale,
  supportedLocales,
}: LocalizationProviderProps) => {
  const [languageAndLocale, setLanguageAndLocale] = useState<LanguageAndLocale>(
    DEFAULT_LANGUAGE_AND_LOCALE,
  );

  useEffect(() => {
    if (locale) {
      setLanguageAndLocale(
        parseLanguagesAndLocales([locale], supportedLocales),
      );
      return;
    }

    setLanguageAndLocale(
      parseLanguagesAndLocales(
        [...window.navigator.languages],
        supportedLocales,
      ),
    );
  }, [locale, supportedLocales]);

  return (
    <ctx.Provider value={languageAndLocale}>
      <VocabProvider
        language={languageAndLocale.language}
        locale={languageAndLocale.locale}
      >
        {children}
      </VocabProvider>
    </ctx.Provider>
  );
};

export const useLocalization = () => useContext(ctx);
